import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// More vibrant color palette
const CHART_COLORS = {
  primary: '#6366F1',    // Indigo
  secondary: '#EC4899',  // Pink
  success: '#10B981',    // Emerald
  warning: '#F59E0B',    // Amber
  danger: '#EF4444',     // Red
  info: '#06B6D4',       // Cyan
  purple: '#8B5CF6',     // Purple
  orange: '#F97316',     // Orange
};

const BUDGET_COLORS = ['#6366F1', '#EC4899', '#10B981'];
const ACTIVITY_COLORS = ['#8B5CF6', '#06B6D4', '#F97316'];
const PIE_COLORS = ['#6366F1', '#E2E8F0'];

export const ProjectDashboard = ({ projectId, stats, onRefresh, project, permissions = [] }) => {
  if (!stats) return <div>Loading stats...</div>;

  // Permission check helper
  const hasPermission = (perm) => {
    // If permissions array is empty or undefined, show all (for backwards compatibility)
    if (!permissions || permissions.length === 0) return true;
    return permissions.includes(perm) || permissions.includes('admin');
  };

  const budgetData = [
    { name: 'Original Budget', value: stats.total_budget || 0, fill: BUDGET_COLORS[0] },
    { name: 'Change Orders', value: stats.total_change_orders || 0, fill: BUDGET_COLORS[1] },
    { name: 'Actual Spend', value: stats.total_spend || 0, fill: BUDGET_COLORS[2] }
  ];

  const activityData = [
    ...(hasPermission('submittals') ? [{ name: 'Submittals', count: stats.submittals_count || 0, fill: ACTIVITY_COLORS[0] }] : []),
    ...(hasPermission('rfis') ? [{ name: 'RFIs', count: stats.rfis_count || 0, fill: ACTIVITY_COLORS[1] }] : []),
    ...(hasPermission('change_orders') ? [{ name: 'Change Orders', count: stats.change_orders_count || 0, fill: ACTIVITY_COLORS[2] }] : [])
  ];

  // Only show pie chart if there's budget data
  const hasBudgetData = (stats.revised_budget || 0) > 0;
  const utilizationData = hasBudgetData ? [
    { name: 'Spent', value: Math.min(stats.budget_utilization || 0, 100) },
    { name: 'Remaining', value: Math.max(0, 100 - (stats.budget_utilization || 0)) }
  ] : [];

  const revisedBudget = stats.revised_budget || (stats.total_budget || 0) + (stats.total_change_orders || 0);
  const remainingBudget = revisedBudget - (stats.total_spend || 0);

  return (
    <div className="space-y-6" data-testid="project-dashboard">
      {/* Project Info Header with Logo */}
      {project && (project.logo_url || project.client_name || project.location) && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100 p-5">
          <div className="flex items-start gap-6">
            {project.logo_url && (
              <img 
                src={project.logo_url} 
                alt="Project Logo" 
                className="w-20 h-20 object-contain rounded-lg border border-slate-200 bg-white p-2"
                data-testid="project-logo"
              />
            )}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              {project.project_number && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Project #</div>
                  <div className="font-bold text-slate-900">{project.project_number}</div>
                </div>
              )}
              {project.client_name && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Client</div>
                  <div className="font-bold text-slate-900">{project.client_name}</div>
                </div>
              )}
              {project.location && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Location</div>
                  <div className="font-bold text-slate-900">{project.location}</div>
                </div>
              )}
              {project.internal_reference && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Internal Ref</div>
                  <div className="font-bold text-slate-900">{project.internal_reference}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Project Overview</h2>
          <p className="text-sm text-slate-600 mt-1">Visual insights and key metrics</p>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
            data-testid="refresh-dashboard-button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {hasPermission('submittals') && (
          <div className="rounded-lg border-l-4 border-l-purple-500 bg-white shadow-sm p-5" data-testid="stat-submittals">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Submittals</div>
            <div className="text-3xl font-bold text-purple-600">{stats.submittals_count || 0}</div>
          </div>
        )}
        {hasPermission('rfis') && (
          <div className="rounded-lg border-l-4 border-l-cyan-500 bg-white shadow-sm p-5" data-testid="stat-rfis">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">RFIs</div>
            <div className="text-3xl font-bold text-cyan-600">{stats.rfis_count || 0}</div>
          </div>
        )}
        {hasPermission('change_orders') && (
          <div className="rounded-lg border-l-4 border-l-orange-500 bg-white shadow-sm p-5" data-testid="stat-change-orders">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Change Orders</div>
            <div className="text-3xl font-bold text-orange-600">{stats.change_orders_count || 0}</div>
          </div>
        )}
        {hasPermission('budget') && (
          <div className="rounded-lg border-l-4 border-l-emerald-500 bg-white shadow-sm p-5" data-testid="stat-utilization">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Budget Utilization</div>
            <div className="text-3xl font-bold text-emerald-600 font-mono">{(stats.budget_utilization || 0).toFixed(1)}%</div>
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget Overview Bar Chart */}
        {hasPermission('budget') && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Budget Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={budgetData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {budgetData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Activity Summary */}
        {activityData.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Activity Summary</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} allowDecimals={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {activityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Budget Utilization Pie Chart */}
        {hasPermission('budget') && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Budget Utilization</h3>
            {hasBudgetData ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={utilizationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                    labelLine={false}
                  >
                    {utilizationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">
                No budget data available
              </div>
            )}
          </div>
        )}

        {/* Financial Summary - Enhanced */}
        {hasPermission('budget') && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Financial Summary</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Original Budget</span>
                <span className="font-mono font-bold text-lg">${(stats.total_budget || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Change Orders</span>
                <span className={`font-mono font-bold text-lg ${(stats.total_change_orders || 0) > 0 ? 'text-amber-600' : ''}`}>
                  {(stats.total_change_orders || 0) > 0 ? '+' : ''}${(stats.total_change_orders || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b bg-slate-50 -mx-6 px-6 py-3">
                <span className="text-slate-900 font-semibold">Revised Budget</span>
                <span className="font-mono font-bold text-xl text-slate-900">${revisedBudget.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-slate-600">Actual Spend</span>
                <span className="font-mono font-bold text-lg">${(stats.total_spend || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2">
                <span className="text-slate-600 font-semibold">Remaining Budget</span>
                <span className={`font-mono font-bold text-xl ${remainingBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {remainingBudget >= 0 ? '' : '-'}${Math.abs(remainingBudget).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Schedule Section */}
      {hasPermission('schedule') && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Tasks */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Upcoming Tasks</h3>
              <div className="flex items-center gap-4 text-sm">
                {(stats.tasks_overdue || 0) > 0 && (
                  <span className="text-red-600 font-medium">{stats.tasks_overdue} Overdue</span>
                )}
                <span className="text-blue-600 font-medium">{stats.tasks_in_progress || 0} In Progress</span>
              </div>
            </div>
            
            {stats.upcoming_tasks && stats.upcoming_tasks.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_tasks.map((task, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      task.status === 'completed' ? 'bg-green-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      task.status === 'delayed' ? 'bg-red-500' : 'bg-slate-300'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{task.description}</div>
                      <div className="text-xs text-slate-500">
                        {task.task_number} • {new Date(task.start_date).toLocaleDateString()} - {new Date(task.end_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${task.progress === 100 ? 'bg-green-500' : 'bg-indigo-500'}`}
                          style={{ width: `${task.progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-8">{task.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No upcoming tasks</p>
                <p className="text-sm mt-1">Add tasks in the Schedule tab</p>
              </div>
            )}
          </div>

          {/* Schedule Gantt Mini Preview */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Schedule Overview</h3>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-900">{stats.schedule_count || 0}</div>
                <div className="text-xs text-slate-500 uppercase font-medium">Total Tasks</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.tasks_completed || 0}</div>
                <div className="text-xs text-slate-500 uppercase font-medium">Completed</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{stats.tasks_in_progress || 0}</div>
                <div className="text-xs text-slate-500 uppercase font-medium">In Progress</div>
              </div>
            </div>

            {/* Mini Gantt visualization */}
            {stats.upcoming_tasks && stats.upcoming_tasks.length > 0 ? (
              <div className="space-y-2">
                {stats.upcoming_tasks.slice(0, 4).map((task, index) => {
                  const today = new Date();
                  const start = new Date(task.start_date);
                  const end = new Date(task.end_date);
                  const totalDays = 30; // 30-day window
                  const startOffset = Math.max(0, Math.ceil((start - today) / (1000 * 60 * 60 * 24)));
                  const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                  const leftPercent = (startOffset / totalDays) * 100;
                  const widthPercent = Math.min((duration / totalDays) * 100, 100 - leftPercent);
                  
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-20 text-xs font-mono text-slate-600 truncate">{task.task_number}</div>
                      <div className="flex-1 h-4 bg-slate-100 rounded relative overflow-hidden">
                        <div 
                          className="absolute h-full rounded"
                          style={{
                            left: `${leftPercent}%`,
                            width: `${Math.max(widthPercent, 5)}%`,
                            backgroundColor: task.status === 'completed' ? '#10B981' : 
                                            task.status === 'in_progress' ? '#6366F1' : '#94A3B8'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>Today</span>
                  <span>+30 days</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500 text-sm">
                No tasks scheduled
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
